import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, Clock, Target, Trophy, Users, Star, Play, MessageSquare, ShoppingCart, Sparkles } from 'lucide-react';
import { bookService } from '../services/bookService';
import { quizService } from '../services/quizService';
import { useToast } from '../hooks/use-toast';
import { bookPath, extractBookId } from '../lib/slug';

const BookDetail = () => {
  const { bookId: rawBookId } = useParams();
  const bookId = extractBookId(rawBookId);
  const { toast } = useToast();
  const [book, setBook] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [relatedBooks, setRelatedBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (bookId) {
      fetchBookData();
      fetchRelatedBooks();
    }
  }, [bookId]);

  const fetchBookData = async () => {
    try {
      setLoading(true);
      const [bookData, quizzesData] = await Promise.all([
        bookService.getBook(bookId),
        quizService.getQuizzesByBook(bookId)
      ]);

      setBook(bookData);
      setQuizzes(quizzesData);
    } catch (error) {
      console.error('Error fetching book data:', error);
      toast({
        title: "Error",
        description: "Failed to load book information.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Recommendations are a secondary concern: if they fail, the rest of the
  // page still renders and the section is simply hidden.
  const fetchRelatedBooks = async () => {
    try {
      const related = await bookService.getRelatedBooks(bookId);
      setRelatedBooks(related);
    } catch (error) {
      console.error('Error fetching related books:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  if (!book) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Book not found</h1>
            <p className="text-gray-600 mb-4">The book you're looking for doesn't exist.</p>
            <Link to="/">
              <Button className="min-h-[44px] touch-manipulation">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // Structured data for the book
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": book.title,
    "author": {
      "@type": "Person",
      "name": book.author
    },
    "genre": book.genre,
    "description": book.description,
    "publisher": book.publisher,
    "datePublished": book.published_year ? `${book.published_year}-01-01` : undefined,
    "numberOfPages": book.page_count,
    "isbn": book.isbn,
    "image": book.cover_url || book.cover_image,
    "url": `${window.location.origin}${bookPath(book)}`,
    "aggregateRating": book.average_rating ? {
      "@type": "AggregateRating",
      "ratingValue": book.average_rating,
      "ratingCount": book.total_attempts || 1
    } : undefined,
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "BookQuiz"
      }
    },
    "mainEntity": {
      "@type": "Quiz",
      "name": `${book.title} Knowledge Quiz`,
      "description": `Test your knowledge of ${book.title} by ${book.author} with our interactive quiz`,
      "numberOfQuestions": quizzes.reduce((total, quiz) => total + (quiz.questions_count || 0), 0),
      "about": {
        "@type": "Book",
        "name": book.title
      }
    }
  };

  const amazonSearchTerm = encodeURIComponent(`${book.title} ${book.author}`);
  const amazonComUrl = `https://www.amazon.com/s?k=${amazonSearchTerm}`;
  const amazonInUrl = `https://www.amazon.in/s?k=${amazonSearchTerm}`;

  const pageTitle = `${book.title} by ${book.author} - Quiz & Knowledge Test | BookQuiz`;
  const pageDescription = `Test your knowledge of "${book.title}" by ${book.author}. Take interactive quizzes, explore themes, and challenge your understanding of this ${book.genre.toLowerCase()} masterpiece.`;
  const pageKeywords = `${book.title}, ${book.author}, book quiz, ${book.genre} quiz, literature test, reading comprehension, ${book.title} questions`;

  return (
    <Layout>
      <SEOHead 
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        url={bookPath(book)}
        canonical={`${window.location.origin}${bookPath(book)}`}
        type="book"
        image={book.cover_url || book.cover_image}
        structuredData={structuredData}
      />

      {/* Back Navigation */}
      <section className="bg-white py-4 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/">
            <Button variant="ghost" className="min-h-[44px] touch-manipulation hover:bg-blue-50">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Books
            </Button>
          </Link>
        </div>
      </section>

      {/* Book Header */}
      <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">
            {/* Book Cover */}
            <div className="lg:col-span-1">
              <div className="relative group">
                <img
                  src={book.cover_url || book.cover_image || `https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=600&fit=crop&q=80`}
                  alt={`${book.title} by ${book.author} - Book cover`}
                  className="w-full max-w-sm mx-auto rounded-xl shadow-2xl transition-transform group-hover:scale-105"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl" />
              </div>
            </div>

            {/* Book Info */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <Badge className="mb-4 bg-blue-100 text-blue-800 border-blue-200">
                  {book.genre}
                </Badge>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                  {book.title}
                </h1>
                <p className="text-xl md:text-2xl text-gray-600 mb-2">by {book.author}</p>
                {book.published_year && (
                  <p className="text-gray-500">Published in {book.published_year}</p>
                )}
                {book.isbn && (
                  <p className="text-gray-500 text-sm">ISBN: {book.isbn}</p>
                )}
              </div>

              <div className="prose prose-lg text-gray-700 leading-relaxed">
                <p>{book.description}</p>
              </div>

              {/* Buy Links */}
              <div className="flex flex-wrap gap-3">
                <a href={amazonComUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="min-h-[44px] touch-manipulation border-orange-200 text-orange-700 hover:bg-orange-50">
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Buy on Amazon.com
                  </Button>
                </a>
                <a href={amazonInUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="min-h-[44px] touch-manipulation border-orange-200 text-orange-700 hover:bg-orange-50">
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Buy on Amazon.in
                  </Button>
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Link to="#quizzes" className="block">
                  <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 text-center shadow-md hover:scale-105 transition-transform cursor-pointer">
                    <Target className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                    <div className="text-xl md:text-2xl font-bold text-gray-900">{book.total_quizzes || 0}</div>
                    <div className="text-sm text-gray-600">Quizzes</div>
                  </div>
                </Link>
                
                <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 text-center shadow-md">
                  <Users className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <div className="text-xl md:text-2xl font-bold text-gray-900">{book.total_attempts || 0}</div>
                  <div className="text-sm text-gray-600">Attempts</div>
                </div>
                
                <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 text-center shadow-md">
                  <Star className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                  <div className="text-xl md:text-2xl font-bold text-gray-900">{book.average_rating || '0'}</div>
                  <div className="text-sm text-gray-600">Rating</div>
                </div>
                
                <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 text-center shadow-md">
                  <Trophy className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <div className="text-xl md:text-2xl font-bold text-gray-900">A+</div>
                  <div className="text-sm text-gray-600">Grade</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quizzes Section */}
      <section id="quizzes" className="py-8 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Available Quizzes for {book.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Test your knowledge with our carefully crafted quizzes. Choose your difficulty level and see how well you understand this masterpiece.
            </p>
          </div>

          {quizzes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {quizzes.map((quiz) => (
                <Card key={quiz.id} className="bg-white/80 backdrop-blur-sm shadow-lg border-0 hover:shadow-xl transition-all hover:scale-105">
                  <CardHeader>
                    <div className="flex justify-between items-start mb-3">
                      <CardTitle className="text-lg font-bold text-gray-900 flex-1 pr-2">
                        {quiz.title}
                      </CardTitle>
                      <Badge className={
                        quiz.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                        quiz.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }>
                        {quiz.difficulty}
                      </Badge>
                    </div>
                    <p className="text-gray-600 text-sm">{quiz.description}</p>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Average Score</span>
                        <span className="font-medium">{quiz.average_score || 0}%</span>
                      </div>
                      <Progress value={quiz.average_score || 0} className="h-2" />
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center">
                        <Target className="h-4 w-4 mr-1" />
                        {quiz.questions_count || 10} questions
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {quiz.time_limit || 15} min
                      </div>
                    </div>
                    
                    <Link to={`/quiz/${quiz.id}`} className="block">
                      <Button className="w-full min-h-[44px] touch-manipulation bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all hover:scale-105 shadow-lg">
                        <Play className="h-4 w-4 mr-2" />
                        Start Quiz
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-xl">
              <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No quizzes available yet</h3>
              <p className="text-gray-600 mb-6">
                This book doesn't have any quizzes yet, but you can request one!
              </p>
            </div>
          )}

          {/* Request Quiz Section */}
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-6 text-center shadow-md">
            <MessageSquare className="h-12 w-12 text-purple-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Want More Quizzes?</h3>
            <p className="text-gray-600 mb-4">
              Request additional quizzes for this book with different difficulty levels
            </p>
            <Link to={`/request-quiz/${book.id}`}>
              <Button variant="outline" className="min-h-[44px] touch-manipulation border-purple-200 text-purple-700 hover:bg-purple-50">
                <MessageSquare className="h-4 w-4 mr-2" />
                Request a Quiz
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Recommendations */}
      {relatedBooks.length > 0 && (
        <section className="py-8 md:py-16 bg-gradient-to-br from-blue-50 to-purple-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                You Might Also Like
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Readers who enjoyed {book.title} tend to reach for these next.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedBooks.map((related) => (
                <Link key={related.id} to={bookPath(related)} className="block">
                  <Card className="h-full bg-white/80 backdrop-blur-sm shadow-lg border-0 hover:shadow-xl transition-all hover:scale-105">
                    <CardContent className="p-5 flex gap-4">
                      <img
                        src={related.cover_url || related.cover_image || `https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=200&h=300&fit=crop&q=80`}
                        alt={`${related.title} by ${related.author} - Book cover`}
                        className="w-20 h-28 object-cover rounded-md shadow-md flex-shrink-0"
                        loading="lazy"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 leading-tight mb-1 line-clamp-2">
                          {related.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">by {related.author}</p>
                        {related.reason && (
                          <p className="text-sm text-gray-700 italic leading-snug mb-3 line-clamp-3">
                            <Sparkles className="h-3 w-3 inline mr-1 text-purple-600" />
                            {related.reason}
                          </p>
                        )}
                        <div className="flex items-center text-sm text-gray-500">
                          <Target className="h-4 w-4 mr-1" />
                          {related.total_quizzes || 0} {related.total_quizzes === 1 ? 'quiz' : 'quizzes'}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default BookDetail;
